/**
 * Stats — resolved stat blocks, the canonical damage formula, and the
 * effective-stat modifier pipeline.
 *
 * Engine-agnostic and Phaser-free so the balance-critical math is unit-testable
 * headlessly (TECHNICAL_ARCHITECTURE §testing). Content carries *partial* stat
 * blocks (zod, see data/schema/common.ts); the engine resolves them to a full
 * `Stats` here, then layers modifiers from external sources on top.
 *
 * The modifier pipeline is the forward-compatibility seam for the layered
 * unit-power system (in-battle tiers → permanent upgrades → hero family
 * masteries → gear/relic affixes). M2 registers ZERO sources, but building the
 * seam now means later layers are "add a modifier source", not a refactor.
 */
import type { DamageType, PartialStatBlock } from '@/data/schema/common';

/** A fully-resolved stat block (all fields present). */
export interface Stats {
  hp: number;
  armor: number; // the single defence: % damage mitigation, 0..0.95
  damage: number;
  damageType: DamageType;
  armorPierce: number; // 0..1, fraction of target armor ignored
  range: number; // px world-space; 0 = melee
  attackSpeed: number; // attacks/sec
  moveSpeed: number; // px/s
  critChance: number; // 0..1
  critMult: number;
  canHitFlyers: boolean;
  chainCount: number; // chain lightning: extra enemies hit (0 = single)
  multiShot: number; // independent targets hit per volley (1 = normal)
}

export const DEFAULT_STATS: Stats = {
  hp: 0,
  armor: 0,
  damage: 0,
  damageType: 'physical',
  armorPierce: 0,
  range: 0,
  attackSpeed: 0,
  moveSpeed: 0,
  critChance: 0,
  critMult: 1.5,
  canHitFlyers: false,
  chainCount: 0,
  multiShot: 1,
};

/**
 * Merge sparse overrides onto a base, last-writer-wins per key (only keys
 * actually present override). This is how upgrade tiers stack: each tier's
 * partial merges onto the resolved previous tier.
 */
export function mergeStats(
  base: Stats,
  ...overrides: PartialStatBlock[]
): Stats {
  const out: Stats = { ...base };
  for (const o of overrides) {
    for (const k of Object.keys(o) as (keyof PartialStatBlock)[]) {
      const v = o[k];
      if (v !== undefined) {
        // Safe: keys of PartialStatBlock are a subset of Stats keys.
        (out as unknown as Record<string, unknown>)[k] = v;
      }
    }
  }
  return out;
}

/** Resolve a unit's stats at a given tier index by cumulatively merging tiers. */
export function resolveTierStats(tiers: PartialStatBlock[], tier: number): Stats {
  const upto = tiers.slice(0, Math.min(tier + 1, tiers.length));
  return mergeStats(DEFAULT_STATS, ...upto);
}

/**
 * Resolve a melee unit's hold capacity at a tier: the last per-tier `blockCount`
 * override at or below `tier`, falling back to the unit-level value. Lets an
 * in-battle upgrade raise how many enemies a blocker stops (e.g. 2 → 3).
 */
export function resolveBlockCount(
  tiers: { blockCount?: number }[],
  tier: number,
  fallback: number,
): number {
  let v = fallback;
  for (let i = 0; i <= Math.min(tier, tiers.length - 1); i++) {
    const o = tiers[i].blockCount;
    if (o != null) v = o;
  }
  return v;
}

/**
 * Resolve a melee unit's squad size (number of soldier bodies) at a tier: the
 * last per-tier `squadSize` override at or below `tier`, falling back to the
 * unit-level value. Lets an in-battle upgrade add another body to the line
 * (the melee mirror of the ranged `multiShot` breakpoint).
 */
export function resolveSquadSize(
  tiers: { squadSize?: number }[],
  tier: number,
  fallback: number,
): number {
  let v = fallback;
  for (let i = 0; i <= Math.min(tier, tiers.length - 1); i++) {
    const o = tiers[i].squadSize;
    if (o != null) v = o;
  }
  return v;
}

// ---- effective-stat modifier pipeline (forward-compat seam) ----------------

/** Numeric stats a modifier can target (excludes damageType/canHitFlyers). */
export type NumericStatKey =
  | 'hp'
  | 'armor'
  | 'damage'
  | 'armorPierce'
  | 'range'
  | 'attackSpeed'
  | 'moveSpeed'
  | 'critChance'
  | 'critMult'
  | 'chainCount'
  | 'multiShot';

/**
 * A single stat modifier from some source (hero mastery, relic affix, aura…).
 * Within a stat, all `addFlat` sum and all `addPct` sum, then we apply
 * `(base + flat) * (1 + pct)` — additive within a category, multiplicative
 * across. Caps keep mitigation/pierce in their valid ranges.
 */
export interface StatModifier {
  stat: NumericStatKey;
  addFlat?: number;
  addPct?: number;
}

const STAT_CAPS: Partial<Record<NumericStatKey, { min?: number; max?: number }>> =
  {
    armor: { min: 0, max: 0.95 },
    armorPierce: { min: 0, max: 1 },
    critChance: { min: 0, max: 1 },
    hp: { min: 0 },
    damage: { min: 0 },
    range: { min: 0 },
    attackSpeed: { min: 0 },
    moveSpeed: { min: 0 },
    critMult: { min: 1 },
    chainCount: { min: 0 },
    multiShot: { min: 1 },
  };

/** Apply external stat modifiers to a resolved stat block. */
export function applyModifiers(base: Stats, mods: StatModifier[]): Stats {
  if (mods.length === 0) return base;

  const flat = new Map<NumericStatKey, number>();
  const pct = new Map<NumericStatKey, number>();
  for (const m of mods) {
    flat.set(m.stat, (flat.get(m.stat) ?? 0) + (m.addFlat ?? 0));
    pct.set(m.stat, (pct.get(m.stat) ?? 0) + (m.addPct ?? 0));
  }

  const out: Stats = { ...base };
  const keys = new Set<NumericStatKey>([...flat.keys(), ...pct.keys()]);
  for (const k of keys) {
    let v = (out[k] + (flat.get(k) ?? 0)) * (1 + (pct.get(k) ?? 0));
    const cap = STAT_CAPS[k];
    if (cap?.min !== undefined) v = Math.max(cap.min, v);
    if (cap?.max !== undefined) v = Math.min(cap.max, v);
    out[k] = v;
  }
  return out;
}

// ---- the canonical damage formula (DATA_SCHEMAS §0) -------------------------

export interface DamageResolution {
  /** Attack damage per hit (pre-crit, pre-mitigation). */
  damage: number;
  damageType: DamageType;
  /** 0..1 fraction of the target's armor ignored. */
  armorPierce: number;
  critMult: number;
  isCrit: boolean;
  /** Defender's armor (the single mitigation stat; already sunder-reduced). */
  targetArmor: number;
}

/**
 * Damage actually dealt after armor/pierce/crit. Armor is the ONE defence stat
 * and mitigates every damage type; `true` damage ignores it. Damage type is
 * flavor (+ a hook for future type-immunities), not a separate channel.
 *
 * A CRITICAL HIT finds the gap in the armor: it ignores armor entirely (full
 * pierce) on top of the crit multiplier. This is the physical attacker's
 * build-dependent answer to armor — armor pierce is the mage's *core* trait,
 * but a crit-stacked Marksman (archers' branch) can shred armor too, spikily.
 *
 *   pierce         = isCrit ? 1 : armorPierce
 *   effectiveArmor = max(0, armor * (1 - pierce))
 *   mitigation     = (damageType === 'true') ? 0 : effectiveArmor
 *   raw            = damage * (isCrit ? critMult : 1)
 *   dealt          = max(1, raw * (1 - mitigation))
 */
export function resolveDamage(r: DamageResolution): number {
  const pierce = r.isCrit ? 1 : r.armorPierce;
  const effectiveArmor = Math.max(0, r.targetArmor * (1 - pierce));
  const mitigation = r.damageType === 'true' ? 0 : effectiveArmor;
  const raw = r.damage * (r.isCrit ? r.critMult : 1);
  return Math.max(1, raw * (1 - mitigation));
}
